import i18n from "@/shared/i18n";
import ReceiptModel from "@/modules/receipt/Receipt.model";
import NoteForm from "@/modules/customer/models/NoteForm.model";
import CustomerService from "@/modules/customer/CustomerService";
import InventoryModel from "@/modules/inventory/Inventory.model";
import {store} from "@/plugins/vuex";
import ReceiptService from "@/modules/receipt/ReceiptService";
import CategoryService from "@/modules/setup/category/CategoryService";
import {Formatter} from "@/core/services/formatter.service";
import UserService from "../../../setup/user/UserService";
import HelperService from "@/core/services/helper.service";


const receiptsActivityHelper = {
  actions: {
    'create.receipt': {
      message: i18n.t('Receipt was created'),
      attributeLabels: new ReceiptModel().attributeLabels,
      excludeAttributes: ['last_item_no']
    },
    'update.receipt': {
      message: i18n.t('Receipt was updated'),
      attributeLabels: new ReceiptModel().attributeLabels,
      excludeAttributes: ['last_item_no']
    },
    'create.inventory': {
      message: i18n.t('Inventory was created'),
      attributeLabels: new InventoryModel().attributeLabels,
      excludeAttributes: ['primary_image', 'active_lot', 'dynamic_fields', 'stock_no']
    },
    'update.inventory': {
      message: i18n.t('Inventory was updated'),
      attributeLabels: new InventoryModel().attributeLabels,
      excludeAttributes: ['primary_image', 'active_lot', 'dynamic_fields']
    },
    'reserved': {
      message: i18n.t('Reserved'),
    },
    'update.receipt_miscellaneous_charges': {
      message: i18n.t('Miscellaneous charges were updated'),
    },
    'create.receipt_commission': {
      message: i18n.t('Commissions were created'),
    },
    'update.receipt_commission': {
      message: i18n.t('Commissions were updated'),
    },
    'create.receipt_note': {
      message: i18n.t('Note was created'),
      attributeLabels: new NoteForm().attributeLabels,
      excludeAttributes: ['receipt']
    },
    'update.receipt_note': {
      message: i18n.t('Note was updated'),
      attributeLabels: new NoteForm().attributeLabels,
      excludeAttributes: ['receipt']
    },
    'delete.receipt_note': {
      message: i18n.t('Note was deleted'),
    },
    'return_note_created': {
      message: i18n.t('Return note was created'),
      attributeLabels: {
        description: i18n.t('Description'),
        is_collected_by_third_party: i18n.t('Collected by 3rd Party'),
        collector: i18n.t('Collector'),
        collector_note: i18n.t('Note'),
        inventories: i18n.t('Inventory Items'),
      }
    },
    'return_note_regenerated': {
      message: i18n.t('Return note was regenerated'),
      attributeLabels: {
        description: i18n.t('Description'),
        is_collected_by_third_party: i18n.t('Collected by 3rd Party'),
        collector: i18n.t('Collector'),
        collector_note: i18n.t('Note'),
        inventories: i18n.t('Inventory Items'),
      }
    },
    'return_note_signed_by_staff': {
      message: i18n.t('Return note was signed by staff'),
    },
    'return_note_signed_by_buyer': {
      message: i18n.t('Return note was signed by buyer'),
    },
    'signed_by_buyer': {
      message: i18n.t('Receipt consignment was signed by buyer'),
    },
    'signed_by_staff': {
      message: i18n.t('Receipt consignment was signed by staff'),
    },
    'receipt_contract_regenerated': {
      message: i18n.t('Receipt consignment was regenerated'),
    },
    'pre_receipt_contract_regenerated': {
      message: i18n.t('Receipt pre-consignment was regenerated'),
    }
  },
  async valueVisualizer(activity, attr, val) {
    let returnValue = val;

    if (activity.name === 'receipt') {
      if (attr === 'date') {
        returnValue = this.$options.filters.toDate(val);
      } else if (attr === 'branch') {
        let branch = this.branches.rows.find(b => b.uuid === val);
        returnValue = branch ? branch.name : '';
      } else if (attr === 'department') {
        let department = this.departments.rows.find(d => d.uuid === val);
        returnValue = department ? Formatter.getByMasterLanguage(department.name) : '';
      } else if (attr === 'customer') {
        let response = await CustomerService.getCustomer(val);
        returnValue = response.body.display_name;
      } else if (attr === 'recommended_by') {
        let response = await CustomerService.getCustomers({'uuid': val});
        returnValue = response.body.results[0].display_name;
      } else if (attr === 'unsold_fee' || attr === 'withdrawal_fee') {
        returnValue = this.$options.filters.asCurrency(Math.round(val))
      } else if (attr === 'insurance_rate') {
        returnValue = this.$options.filters.asPercentage(Math.round(val));
      } else if (attr === 'minimum_commission') {
        returnValue = this.$options.filters.asCurrency(Math.round(val));
      } else if (attr === 'lot_handling_charge') {
        returnValue = this.$options.filters.asCurrency(Math.round(val))
      } else if (attr === 'specialists') {
        returnValue = await getSpecialists(val, returnValue);
      }
    } else if (activity.name === 'inventory') {
      if (attr === 'branch') {
        let branch = store.state.setup.branch.branches.rows.find(b => b.uuid === val);
        returnValue = branch ? branch.name : '';
      } else if (attr === 'customer') {
        let response = await CustomerService.getCustomer(val);
        returnValue = response.body.display_name;
      } else if (attr === 'receipt') {
        let response = await ReceiptService.getReceipt(val);
        returnValue = response.body.receipt_no;
      } else if (attr === 'category') {
        let response = await CategoryService.getCategory(val);
        returnValue = Formatter.getByMasterLanguage(response.body.name);
      } else if (attr === 'department') {
        let department = store.state.setup.department.departments.rows.find(d => d.uuid === val);
        returnValue = department ? Formatter.getByMasterLanguage(department.name) : '';
      } else if (attr === 'status') {
        returnValue = val.charAt(0).toUpperCase() + val.slice(1);
      } else if (attr === 'tax_status') {
        const taxStatus = store.state.global.taxStatuses.find(ts => ts.uuid === val);
        if (taxStatus) {
          returnValue = taxStatus.name;
        }
      } else if (attr === 'specialists') {
        returnValue = await getSpecialists(val, returnValue);
      } else if (attr === 'item_location') {
        const location = HelperService.searchNested(store.state.global.itemLocations, val, 'uuid', 'children')
        if (location) returnValue = location.name
      }
    }
    async function getSpecialists(val, returnValue) {
      if (val.length) {
        let response = await UserService.getUsers({uuids: val.join(',')});
        const specialists = [];
        if (response && response.body && response.body.results) {
          response.body.results.forEach(u => specialists.push(u.display_name));
        }
        returnValue = specialists.join('</br>');
      }
      return returnValue;
    }
    return returnValue;
  },
}


export default receiptsActivityHelper;
